import { useDispatch, useSelector } from 'react-redux';
import VisibleIf from '../../../components/VisibleIf';
import { getFormActions } from '../../../utils/form-helpers';
import formConfig from '../../../payment/invoice-form-config';
import EmailSuggestion from '../../../components/EmailSuggestion';
import styles from './invoice.module.less';
import Input2 from '../../../components/input/input-2/Input2';
import OrganizationNumberInput from '../../../components/input/OrganizationNumberInput';
import { useProject } from '../../project-hooks/useProject';
import { fetchOrganizationFromExternalSource } from '../../../payment/invoice-actions';
import { Input, InputGroup, Checkbox } from '@sb1/ffe-form-react';
import { ContextMessage } from '@sb1/ffe-messages-react';
import { Heading4 } from '@sb1/ffe-core-react';
import { PAYMENT_METHOD_INVOICE } from '../payment-methods/PaymentMethods';
import { paymentMethodTitle } from '../payment-methods/paymentMethodUtils';

const { setFormField, setFormFields } = getFormActions(formConfig);

const Invoice = () => {
    const dispatch = useDispatch();
    const project = useProject();
    const eventId = project?.event_id;
    const showNameAndComment = project?.profile?.anonymousGiver;

    const tvaEvent = useSelector((state) => state.campaigns.tvaEventId);
    const p3aEvent = useSelector((state) => state.campaigns.p3aEventId);

    const invoiceForm = useSelector((state) => state.invoiceForm);

    const isTva = eventId && [p3aEvent, tvaEvent].includes(eventId.toString());
    const { errors = {} } = invoiceForm;

    return (
        <div>
            <div className={styles.invoiceFormWrapper}>
                <Heading4 lookLike={5}>{paymentMethodTitle(PAYMENT_METHOD_INVOICE)}</Heading4>
                <VisibleIf isVisible={showNameAndComment}>
                    <ContextMessage type="info" compact className={styles.invoiceInfoMessage}>
                        Bidraget ditt blir synlig på spleisen først etter at du har betalt fakturaen. Hvis du ønsker
                        synlighet på spleisen umiddelbart, kan du betale med kort eller Vipps.
                    </ContextMessage>
                </VisibleIf>
                <form className={styles.invoiceForm}>
                    <div>
                        <div>
                            <Input2
                                title="E-post"
                                fieldName="email"
                                value={invoiceForm.email}
                                errors={errors}
                                type="email"
                                onChange={(e) => dispatch(setFormField('email', e.target.value.trim(), false))}
                                onBlur={(e) => dispatch(setFormField('email', e.target.value.trim(), true))}
                            />
                            <EmailSuggestion className={styles.emailSuggestion} email={invoiceForm.email} />
                        </div>
                        <OrganizationNumberInput
                            title="Organisasjonsnummer"
                            fieldName="organizationNumber"
                            value={invoiceForm.organizationNumber}
                            error={errors.organizationNumber || errors.name}
                            onChange={({ value }) => {
                                const organizationNumber = value ? value.replace(/\s/g, '') : null;
                                dispatch(
                                    setFormFields(
                                        {
                                            organizationNumber,
                                            name: null,
                                            address1: null,
                                            postalPlace: null,
                                            postalCode: null,
                                        },
                                        false
                                    )
                                );
                                dispatch(fetchOrganizationFromExternalSource(organizationNumber));
                            }}
                            onBlur={() =>
                                dispatch(setFormField('organizationNumber', invoiceForm.organizationNumber, true))
                            }
                        />
                        {invoiceForm.name && (
                            <div className={styles.organizationAddressInfo}>
                                <div>{invoiceForm.name}</div>
                                <div>{invoiceForm.address1}</div>
                                <div>{invoiceForm.postalCode + ' ' + invoiceForm.postalPlace}</div>
                            </div>
                        )}
                        {isTva && (
                            <p className={styles.invoiceShareDataInfo}>
                                E-postadresse, beløp og organisasjonsnummer deles med TV-aksjonen.
                            </p>
                        )}
                        <div>
                            <Input2
                                title="Kontaktperson i bedriften (valgfritt)"
                                fieldName="contactPersonAtCompany"
                                value={invoiceForm.contactPersonAtCompany}
                                errors={errors}
                                type="text"
                                maxLength={100}
                                onChange={(e) =>
                                    dispatch(setFormField('contactPersonAtCompany', e.target.value, false))
                                }
                                onBlur={(e) => dispatch(setFormField('contactPersonAtCompany', e.target.value, true))}
                            />
                        </div>
                        <div>
                            <InputGroup
                                label={'Kommentar (valgfritt)'}
                                description={'Dette kan f.eks. være ordrenummer, ressursnummer, PO-nummer, etc.'}
                                fieldMessage={errors?.['resourceNumber'] || undefined}
                            >
                                <Input
                                    name={'resourceNumber'}
                                    value={invoiceForm.resourceNumber}
                                    maxLength={100}
                                    onChange={(e) => dispatch(setFormField('resourceNumber', e.target.value, false))}
                                    onBlur={(e) => dispatch(setFormField('resourceNumber', e.target.value, true))}
                                />
                            </InputGroup>
                        </div>
                        <div className={styles.sendAsEhfOption}>
                            <Checkbox
                                name="sendAsEhf"
                                checked={invoiceForm.sendAsEhf}
                                onChange={(e) => dispatch(setFormField('sendAsEhf', e.target.checked))}
                            >
                                Motta som EHF-faktura
                            </Checkbox>
                        </div>
                        {!!project?.profile?.invoiceTaxDeduction && (
                            <>
                                <div className={styles.taxDeductionOption}>
                                    <Checkbox
                                        name="storeGiverData"
                                        checked={invoiceForm.taxDeduction}
                                        onChange={(e) => dispatch(setFormField('taxDeduction', e.target.checked))}
                                    >{`Del organisasjonsnummer med ${
                                        project?.receiver?.name || 'mottaker'
                                    } for mulighet til å få skattefradrag`}</Checkbox>
                                </div>
                                {invoiceForm.taxDeduction && (
                                    <ContextMessage type="info" compact className={styles.taxDeductionInfo}>
                                        Det er {project?.receiver?.name || 'mottaker'} som rapporterer gaven til
                                        skattemyndighetene.
                                    </ContextMessage>
                                )}
                            </>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Invoice;
